/**
 * Media Gallery Modal Web Component
 * 
 * Features:
 * - Custom web component for modular reuse
 * - Works with pre-rendered content from Liquid
 * - Navigation with arrows, keyboard, and swipe
 * - Thumbnail navigation
 * - Support for both images and videos
 * - Improved accessibility with focus management
 */

// Define the MediaGalleryModal web component
class MediaGalleryModal extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.triggerElement = null;
    this.focusableElements = null;
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;
    
    // Initialize modal elements after DOM is fully loaded
    this.initialized = false;
  }
  
  connectedCallback() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeElements());
    } else {
      this.initializeElements();
    }
  }
  
  initializeElements() {
    if (this.initialized) return;
    
    // Initialize modal elements
    this.backdrop = this.querySelector('.modal-backdrop');
    this.closeButton = this.querySelector('.modal-close');
    this.mediaDisplay = this.querySelector('.media-display-area');
    this.mediaItems = this.querySelectorAll('.modal-media-item');
    this.thumbnailsContainer = this.querySelector('.modal-thumbnails');
    this.thumbnails = this.querySelectorAll('.modal-thumbnail');
    this.prevButton = this.querySelector('.nav-button.prev');
    this.nextButton = this.querySelector('.nav-button.next');
    
    // Bind event handlers
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.close = this.close.bind(this);
    this.showMedia = this.showMedia.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.trapFocus = this.trapFocus.bind(this);
    
    // Add event listeners
    this.closeButton.addEventListener('click', this.close);
    this.backdrop.addEventListener('click', this.close);
    this.prevButton.addEventListener('click', () => this.navigate(-1));
    this.nextButton.addEventListener('click', () => this.navigate(1));
    
    // Add thumbnail click events
    this.thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', () => {
        const index = parseInt(thumbnail.dataset.index, 10);
        this.showMedia(index);
      });
    });
    
    // Touch events for swipe
    this.mediaDisplay.addEventListener('touchstart', this.handleTouchStart, {passive: true});
    this.mediaDisplay.addEventListener('touchmove', this.handleTouchMove, {passive: true});
    this.mediaDisplay.addEventListener('touchend', this.handleTouchEnd);
    
    this.initialized = true;
  }
  
  initialize(startIndex = 0, triggerElement = null) {
    if (!this.initialized) {
      this.initializeElements();
    }
    
    // Store the element that triggered the modal
    this.triggerElement = triggerElement;
    
    this.currentIndex = startIndex;
    
    // Show initial media
    this.showMedia(this.currentIndex);
    
    // Update ARIA attributes
    this.setAttribute('aria-hidden', 'false');
    
    // Get all focusable elements in the modal
    this.updateFocusableElements();
    
    // Add keyboard event listeners
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keydown', this.trapFocus);
    
    // Show modal
    this.style.display = 'block';
    document.body.style.overflow = 'hidden';
    
    // Set focus to the close button after a short delay to ensure rendering
    setTimeout(() => {
      this.closeButton.focus();
    }, 50);
  }
  
  updateFocusableElements() {
    // Collect all focusable elements within the modal
    this.focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (this.focusableElements.length) {
      this.firstFocusableElement = this.focusableElements[0];
      this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
    }
  }
  
  trapFocus(event) {
    // Skip if not Tab key
    if (event.key !== 'Tab') return;
    
    // Update focusable elements in case DOM has changed
    this.updateFocusableElements();
    
    if (!this.focusableElements.length) return;
    
    const isTabPressed = event.key === 'Tab';
    
    // If shift + tab and focus is on first element, move to last element
    if (event.shiftKey && document.activeElement === this.firstFocusableElement) {
      event.preventDefault();
      this.lastFocusableElement.focus();
    }
    
    // If tab and focus is on last element, move to first element
    if (!event.shiftKey && document.activeElement === this.lastFocusableElement) {
      event.preventDefault();
      this.firstFocusableElement.focus();
    }
  }
  
  showMedia(index) {
    if (index < 0 || index >= this.mediaItems.length) return;
    
    this.currentIndex = index;
    
    // Hide all media items
    this.mediaItems.forEach(item => {
      item.style.display = 'none';
      // Update ARIA attributes
      item.setAttribute('aria-hidden', 'true');
    });
    
    // Show selected media item
    const selectedItem = this.mediaItems[index];
    selectedItem.style.display = 'flex';
    selectedItem.setAttribute('aria-hidden', 'false');
    
    // Handle videos - pause all videos and play current if it's a video
    const videos = this.querySelectorAll('video');
    videos.forEach(video => video.pause());
    
    const currentVideo = selectedItem.querySelector('video');
    if (currentVideo) {
      // Reset the video to beginning
      currentVideo.currentTime = 0;
      // Optionally autoplay when selected
      // currentVideo.play();
    }
    
    // Update thumbnails
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === index);
      thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });

    setTimeout(() => {
      // Set focus to the selected media item
      selectedItem.focus();
    }, 100); // Allow reflow for transition
  }
  
  navigate(step) {
    const newIndex = this.currentIndex + step;
    
    // Wrap around to the start/end if needed
    if (newIndex < 0) {
      this.showMedia(this.mediaItems.length - 1);
    } else if (newIndex >= this.mediaItems.length) {
      this.showMedia(0);
    } else {
      this.showMedia(newIndex);
    }
  }
  
  handleKeyDown(event) {
    switch(event.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowLeft':
        this.navigate(-1);
        break;
      case 'ArrowRight':
        this.navigate(1);
        break;
    }
  }
  
  handleTouchStart(event) {
    this.touchStartX = event.changedTouches[0].screenX;
  }
  
  handleTouchMove(event) {
    // Track finger position but don't do anything yet
    this.touchMoveX = event.changedTouches[0].screenX;
  }
  
  handleTouchEnd(event) {
    this.touchEndX = event.changedTouches[0].screenX;
    this.handleSwipe();
  }
  
  handleSwipe() {
    const swipeThreshold = 50;
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    if (swipeDistance > swipeThreshold) {
      // Swiped right, go to previous
      this.navigate(-1);
    } else if (swipeDistance < -swipeThreshold) {
      // Swiped left, go to next
      this.navigate(1);
    }
  }
  
  close() {
    // Hide modal
    this.style.display = 'none';
    document.body.style.overflow = '';
    
    // Stop any playing videos
    const videos = this.querySelectorAll('video');
    videos.forEach(video => video.pause());
    
    // Remove keyboard event listeners
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keydown', this.trapFocus);

    // Return focus to the element that triggered the modal
    if (this.triggerElement && this.triggerElement.focus) {
      setTimeout(() => {
        this.triggerElement.focus();

        // Update ARIA attributes
        this.setAttribute('aria-hidden', 'true');
      }, 50);
    }
  }
}

// Register the web component
customElements.define('media-gallery-modal', MediaGalleryModal);